import { Quat } from '@/core/math/Quat';
import { Vec3 } from '@/core/math/Vec3';

/**
 * The pose of a reference frame expressed in its parent frame at some instant.
 * `orientation` rotates vectors from this frame into the parent.
 */
export interface FrameState {
  position: Vec3;
  velocity: Vec3;
  orientation: Quat;
  /** Rotation rate of this frame, expressed in the parent frame (rad/s). */
  angularVelocity: Vec3;
}

export function identityFrameState(): FrameState {
  return {
    position: Vec3.zero(),
    velocity: Vec3.zero(),
    orientation: Quat.identity(),
    angularVelocity: Vec3.zero(),
  };
}

/**
 * A node in the reference-frame hierarchy.
 *
 *   solar-system inertial (root)
 *     └── planet-centred inertial
 *           ├── planet body-fixed (rotates with the surface)
 *           │     └── regional / local tangent frames
 *           └── moon-centred inertial
 *
 * Frames are *evaluated*, not stored: `stateAt(t)` is a pure function of
 * simulation time, which is what makes the universe reproducible and makes
 * analytical time warp possible.
 */
export interface ReferenceFrame {
  readonly id: string;
  readonly parentId: string | null;
  /** Human-readable label for diagnostics. */
  readonly label: string;
  /** Pose of this frame within its parent at simulation time `t` (seconds). */
  stateAt(t: number): FrameState;
}

/** A frame that never moves relative to its parent. */
export class StaticFrame implements ReferenceFrame {
  constructor(
    public readonly id: string,
    public readonly parentId: string | null,
    public readonly label: string,
    private readonly state: FrameState = identityFrameState(),
  ) {}

  stateAt(): FrameState {
    return this.state;
  }
}

/** A frame whose pose is supplied by a callback (planets, moons, stations). */
export class DynamicFrame implements ReferenceFrame {
  constructor(
    public readonly id: string,
    public readonly parentId: string | null,
    public readonly label: string,
    private readonly evaluate: (t: number) => FrameState,
  ) {}

  stateAt(t: number): FrameState {
    return this.evaluate(t);
  }
}
